import { z } from 'zod';
import { contactStatusSchema } from './contacts.js';
import { counter } from './counts.js';
/**
 * Importing contacts from a file.
 *
 * The browser parses the CSV and posts batches of rows. Not the whole file:
 * a 100MB upload in one request is a request that times out, a memory spike on
 * the server, and no way to show progress. Batches give all three back.
 *
 * Addresses are NOT validated by this schema. They are checked by the domain's
 * own `parseEmailAddress`, so a bad row comes back with a line number and a
 * reason a person can act on — rather than the whole batch being rejected with
 * a validation error that names no line at all.
 */
/**
 * One batch. Large enough that forty thousand rows is a few hundred requests,
 * small enough that a failed one is cheap to retry.
 */
export const IMPORT_BATCH_SIZE = 500;
export const importRequestSchema = z.object({
    /** The file name, recorded as provenance in the consent log. */
    source: z.string().trim().min(1).max(255),
    /**
     * Segments every contact in this batch is connected to.
     *
     * Written in the same transaction as the rows, so "add this person to the
     * newsletter" is one act rather than a create followed by a connect that can
     * fail on its own and leave a contact nobody meant to add loose.
     *
     * Only ever applied to rows that were *inserted*. An address already on the
     * list keeps the segments it has, for the same reason it keeps its
     * subscription state: a file must not quietly rearrange somebody's audience.
     */
    segmentIds: z.array(z.string().uuid()).max(50).optional(),
    rows: z
        .array(z.object({
        email: z.string().max(320),
        name: z.string().max(200),
        /** A column rather than a declared field, so it is named rather than in `custom`. */
        company: z.string().max(200).optional(),
        /**
         * What this contact's subscription state starts as.
         *
         * Absent is `subscribed`, which is what every row meant before this
         * existed. It is applied on insert only — `on conflict do nothing`
         * means an address already on the list keeps the state it has, so
         * neither a file nor a form can quietly resubscribe somebody who asked
         * to be left alone.
         */
        status: contactStatusSchema.optional(),
        /**
         * Values for fields this organisation declared, keyed by field key.
         *
         * The browser matches a column header to a declared field and fills
         * this in; a header matching nothing is not sent. Keys the server
         * does not recognise are dropped rather than stored, so a stale
         * bundle cannot write a field that no longer exists.
         */
        custom: z.record(z.string().max(40), z.string().max(500)).optional(),
    }))
        .min(1)
        .max(IMPORT_BATCH_SIZE),
});
export const importResponseSchema = z.object({
    added: counter,
    /** Already on the list, and deliberately left exactly as they were. */
    duplicates: counter,
    /** Capped sample, so a wholly malformed file does not return every line. */
    rejected: z.array(z.object({ line: z.number().int().min(1), reason: z.string() })),
    rejectedTotal: counter,
    /**
     * Columns that gained an option because this batch held a value they did not
     * offer, named as a person reads them.
     *
     * A dropdown or a status is declared with a name and a kind, and its options
     * appear as data arrives. Reported rather than done quietly: somebody needs
     * to see what their file taught a column — and when a column had no room
     * left, why the board is showing values it does not offer.
     */
    optionsCreated: z.array(z.object({
        column: z.string().max(120),
        options: z.array(z.string().max(60)),
        /** Values the column could not take: it is full, or they are too long. */
        noRoom: counter,
    })),
});
//# sourceMappingURL=import.js.map